// Types (replacing enums for erasableSyntaxOnly compatibility)
export type IdentityType = "individual" | "business";
export const IdentityType = {
  INDIVIDUAL: "individual",
  BUSINESS: "business",
} as const;

export type IdentityStatus = "ACTIVE" | "PENDING_REVIEW" | "REJECTED";
export const IdentityStatus = {
  ACTIVE: "ACTIVE",
  PENDING_REVIEW: "PENDING_REVIEW",
  REJECTED: "REJECTED",
} as const;

export type KycStatus = "PENDING" | "IN_REVIEW" | "VERIFIED" | "REJECTED" | "EXPIRED";
export const KycStatus = {
  PENDING: "PENDING",
  IN_REVIEW: "IN_REVIEW",
  VERIFIED: "VERIFIED",
  REJECTED: "REJECTED",
  EXPIRED: "EXPIRED",
} as const;

export type KycLevel = "NN" | "basic" | "medium" | "full";
export const KycLevel = {
  NN: "NN",
  BASIC: "basic",
  MEDIUM: "medium",
  FULL: "full",
} as const;

export type AddressType = "SHIPPING" | "BILLING" | "REGISTRATION" | "KYC";
export const AddressType = {
  SHIPPING: "SHIPPING",
  BILLING: "BILLING",
  REGISTRATION: "REGISTRATION",
  KYC: "KYC",
} as const;

export type UserType = "internal" | "organization_member";
export const UserType = {
  INTERNAL: "internal",
  ORGANIZATION_MEMBER: "organization_member",
} as const;

export type UserRole = "admin" | "operator" | "support" | "developer" | "user";
export const UserRole = {
  ADMIN: "admin",
  OPERATOR: "operator",
  SUPPORT: "support",
  DEVELOPER: "developer",
  USER: "user",
} as const;

export type UserStatus = "active" | "inactive" | "suspended" | "pending_verification";
export const UserStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  SUSPENDED: "suspended",
  PENDING_VERIFICATION: "pending_verification",
} as const;

export type OrganizationStatus = "active" | "inactive" | "suspended" | "pending";
export const OrganizationStatus = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  SUSPENDED: "suspended",
  PENDING: "pending",
} as const;

export type Provider = "BLOQUE" | "CRYPTO_MATE";
export const Provider = {
  BLOQUE: "BLOQUE",
  CRYPTO_MATE: "CRYPTO_MATE",
} as const;

// Interfaces relacionadas
export interface Address {
  id: string; // UUID
  address_line_1: string;
  address_line_2: string | null;
  city: string;
  state: string;
  country: string;
  type: AddressType;
  created_at: string; // ISO 8601 date-time
  updated_at: string; // ISO 8601 date-time
}

export interface UserEntity {
  id: string; // UUID
  full_name: string;
  email: string;
  password?: string; // Solo en algunos contextos
  email_verified_at: string | null; // ISO 8601 date-time
  user_type: UserType;
  organization_id: string | null; // UUID
  organization?: Organization | null;
  role: UserRole;
  scopes: string[]; // Array de permisos
  status: UserStatus;
  last_login_at: string | null; // ISO 8601 date-time
  profile_image: string | null;
  country: string | null;
  phone_number: string | null;
  preferred_language: string | null;
  timezone: string | null;
  two_factor_enabled: boolean;
  two_factor_secret: string | null;
  failed_login_attempts: number;
  locked_until: string | null; // ISO 8601 date-time
  password_changed_at: string | null; // ISO 8601 date-time
  must_change_password: boolean;
  reset_password_token: string | null;
  coloca_php_api_key: string | null;
  reset_password_expires: string | null; // ISO 8601 date-time
  created_at: string; // ISO 8601 date-time
  updated_at: string; // ISO 8601 date-time
  deleted_at: string | null; // ISO 8601 date-time
}

export interface Organization {
  id: string; // UUID
  name: string;
  slug: string | null;
  email: string;
  phone: string | null;
  status: OrganizationStatus;
  created_by_user_id: string; // UUID
  created_by?: UserEntity | null;
  users?: UserEntity[] | null;
  card_provider: Provider | null;
  webhook_urls: string[] | null;
  webhook_secret: string | null;
  card_styles_config: {
    backgroundColor?: string;
    textColor?: string;
    panFontSize?: string;
    nameFontSize?: string;
    borderRadius?: string;
    boxShadow?: string;
    cardWidth?: string;
    cardHeight?: string;
    logoUrl?: string;
  } | null;
  organization_identity_id: string | null; // UUID
  organization_identity?: Identity | null;
  created_at: string; // ISO 8601 date-time
  updated_at: string; // ISO 8601 date-time
  deleted_at: string | null; // ISO 8601 date-time
}

export interface IdentityMetadata {
  captured_from_provider?: {
    urn?: string;
    type?: string;
    origin?: string;
    status?: string;
    profile?: {
      city?: string;
      email?: string;
      phone?: string;
      state?: string;
      gender?: string;
      birthdate?: string;
      last_name?: string;
      first_name?: string;
      postal_code?: string;
      address_line1?: string;
      address_line2?: string;
      personal_id_type?: string;
      personal_id_number?: string;
      country_of_birth_code?: string;
      country_of_residence_code?: string;
    };
    metadata?: Record<string, unknown>;
  };
  custom_fields?: Record<string, unknown>;
  audit_trail?: Array<{
    action: string;
    timestamp: string;
    user_id?: string;
  }>;
}

export interface IdentityProviderReference {
  id: string;
  identity_id: string;
  provider: Provider;
  provider_identity_id: string;
  created_at: string;
  updated_at: string;
}

// Estructura principal de Identity (API Response)
export interface Identity {
  id: string; // UUID
  type: IdentityType; // 'individual' | 'business'
  status: IdentityStatus; // 'ACTIVE' | 'PENDING_REVIEW' | 'REJECTED'
  created_by_user_id: string; // UUID
  created_by_user: UserEntity; // Relación cargada
  organization_id: string | null; // UUID (nullable)
  organization: Organization | null; // Relación cargada (nullable)
  address_id: string; // UUID
  address: Address; // Relación cargada
  email: string;
  phone: string;
  kyc_status: KycStatus; // 'PENDING' | 'IN_REVIEW' | 'VERIFIED' | 'REJECTED' | 'EXPIRED'
  kyc_level: KycLevel; // 'NN' | 'basic' | 'medium' | 'full'
  kyc_verified_at: string | null; // ISO 8601 date-time (nullable)
  kyc_rejection_reason: string | null; // (nullable)
  kyc_document_url: string | null; // (nullable)
  /**
   * @deprecated The KYC provider's own hosted link, frozen when the applicant was registered and never
   * refreshed — its TTL is minutes, so it is already dead on any normal read. Never render it: it is a
   * third party's URL (rule 12). Use `active_kyc_link`.
   */
  kyc_link?: string | null;
  /**
   * Coloca's own verification link (`/verify/:token`), read from `identity_kyc_links` and only returned while
   * it is still usable — the backend checks both `status = ACTIVE` and `expires_at > now`. Absent means
   * "there is no link to hand out", which is a normal state, not an error.
   */
  active_kyc_link?: string | null;
  /** ISO 8601 — when `active_kyc_link` stops working. Absent whenever the link is. */
  active_kyc_link_expires_at?: string | null;
  is_organization_identity: boolean;
  metadata: IdentityMetadata | null; // JSONB (nullable)
  provider?: IdentityProviderReference | null; // Relación OneToOne (nullable, normalmente no se carga en findAll)
  created_at: string; // ISO 8601 date-time
  updated_at: string; // ISO 8601 date-time
  deleted_at: string | null; // ISO 8601 date-time (nullable)
  // Dedupe key + document (shared by individual and business)
  country?: string; // ISO3 — document-issuing country
  identification_number?: string;
  identification_type?: string; // 'CC', 'CE', 'PPT', 'PASSPORT', 'NIT', ...
  // Individual-specific fields
  first_name?: string;
  last_name?: string;
  birthdate?: string; // ISO date format
  gender?: string; // 'M' | 'F' | etc.
  nationality?: string; // Country code (COL, MEX, etc.)
  // Business-specific fields. Note: the qa entity stores the legal name
  // (`legal_name`) in the `first_name` column; there is no `legal_name` column.
  business_type?: string;
  incorporation_date?: string; // ISO date format
  logo?: string;
  owner_name?: string;
  owner_id_type?: string;
  owner_id_number?: string;
  owner_address_line1?: string;
  owner_city?: string;
  owner_state?: string;
  owner_postal_code?: string;
  owner_country_code?: string;
  // Only populated when the endpoint is called with `with_aggregates=true`.
  cards_count?: number;
}

// Estructura de respuesta de la API (basada en el payload mostrado)
export interface IdentitiesListApiResponse {
  data: Identity[];
  total?: number;
  skip?: number;
  limit?: number;
  hasMore?: boolean;
}

// Alternativa con paginación estilo Laravel (si la API usa ese formato)
export interface IdentitiesPaginatedApiResponse {
  data: Identity[];
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number;
  to: number;
}

// Tipo unificado que puede recibir cualquier formato de respuesta
export interface IdentitiesApiResponse {
  data: Identity[];
  total?: number;
  skip?: number;
  limit?: number;
  hasMore?: boolean;
  // Paginación estilo Laravel (alternativa)
  current_page?: number;
  per_page?: number;
  last_page?: number;
  from?: number;
  to?: number;
}

/**
 * KYC presentation bucket, mirroring `IdentityKycGroup` in coloca-nest.
 *
 * Not a subset of `KycStatus`: `pending` also covers identities with no KYC provider row yet. The grouping
 * rule lives on the backend precisely so this union can stay a plain wire vocabulary — see `kycStatusMeta`
 * for the per-row presentation of the same idea.
 */
export type IdentityKycGroup = "approved" | "pending" | "rejected";

/**
 * Query params accepted by `GET /internal/identities`.
 *
 * This mirrors `IdentitiesQueryDto` exactly, and it has to: the backend runs a global `ValidationPipe` with
 * `forbidNonWhitelisted`, so an unknown param is a **400**, not a silently ignored one. This type used to
 * advertise `page`, `per_page`, `nationality` and `kyc_status`, none of which the API accepts — a trap for
 * anyone who trusted it.
 *
 * Every value must be spread conditionally when building a request: `buildQueryString` drops
 * `null`/`undefined` but NOT empty strings, so `{ search: "" }` would emit `?search=` and fail validation.
 */
export interface IdentitiesFilters {
  limit?: number;
  skip?: number;
  /** Partial, case-insensitive match over names, document number and business owner. Min 3 chars. */
  search?: string;
  type?: IdentityType;
  status?: IdentityStatus;
  kyc_group?: IdentityKycGroup;
  /** Ignored for organization members — the backend pins them to their own org. */
  organization_id?: string;
  // Opt-in: when true, the response carries `cards_count` per identity.
  with_aggregates?: boolean;
}

/**
 * Response of `GET /internal/identities/counts` — row counts per KYC bucket for the same filters the listing
 * applies, so the filter chips can be labelled without paging through the result set.
 */
export interface IdentitiesCountsResponse {
  all: number;
  approved: number;
  pending: number;
  rejected: number;
}

// KYC link regeneration — POST /internal/identities/verification/:id/kyc-link
export interface RegenerateKycLinkResponse {
  verification_url: string;
  identity_token: string;
  expires_at: string; // ISO timestamp
}

// Internal mail — POST /internal/mail/send
export type OperationsKycVerificationMailType = "OPERATIONS_KYC_VERIFICATION";

export interface OperationsKycVerificationMailPayload {
  name: string;
  verificationUrl: string;
  expiresInHours?: string;
}

export interface SendKycVerificationMailRequest {
  type: OperationsKycVerificationMailType;
  to: string;
  payload: OperationsKycVerificationMailPayload;
}

// Tipo simplificado para uso en componentes (solo campos necesarios para la tabla)
export interface IdentityTableRow {
  id: string;
  name: string; // Computado: first_name + last_name o nombre de empresa
  document_number: string; // identification_number
  nationality: string; // nationality code
  type: IdentityType;
  status: IdentityStatus;
  created_at?: string | null;
  updated_at?: string | null;
}
